/**
 * User Preferences Form Component
 *
 * Form for updating user preferences (theme, notifications, date/time format)
 */

import { zodResolver } from '@hookform/resolvers/zod'
import {
  type UpdateUserPreferences,
  UpdateUserPreferencesSchema,
  type UserResponse,
} from '@monorepo/shared/schemas/users'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Monitor, Moon, Sun } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from '@/lib/toast'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Switch } from '@/shared/components/ui/switch'
import { cn } from '@/shared/lib/utils'
import { userService } from '../services/userService'

interface UserPreferencesFormProps {
  user: UserResponse
  className?: string
}

export function UserPreferencesForm({
  user,
  className,
}: UserPreferencesFormProps) {
  const queryClient = useQueryClient()

  const {
    handleSubmit,
    formState: { isValid, isDirty },
    watch,
    setValue,
  } = useForm<UpdateUserPreferences>({
    resolver: zodResolver(UpdateUserPreferencesSchema) as never,
    mode: 'onChange',
    defaultValues: {
      theme: user.preferences?.theme || 'system',
      notifications: {
        email: user.preferences?.notifications?.email ?? true,
        push: user.preferences?.notifications?.push ?? true,
        in_app: user.preferences?.notifications?.in_app ?? true,
      },
      dashboard_layout: user.preferences?.dashboard_layout || 'grid',
      date_format: user.preferences?.date_format || 'MM/DD/YYYY',
      time_format: user.preferences?.time_format || '12h',
    },
  })

  const updatePreferencesMutation = useMutation({
    mutationFn: (data: UpdateUserPreferences) =>
      userService.updatePreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      toast.success('Preferences updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update preferences')
    },
  })

  const onSubmit = async (data: UpdateUserPreferences) => {
    await updatePreferencesMutation.mutateAsync(data)
  }

  const themeIcons = {
    light: <Sun className='h-5 w-5' />,
    dark: <Moon className='h-5 w-5' />,
    system: <Monitor className='h-5 w-5' />,
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex flex-col gap-6', className)}
    >
      <Card className='bg-transparent'>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>
            Customize how the app looks and feels
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Theme */}
          <div className='grid gap-2'>
            <Label>Theme</Label>
            <div className='grid grid-cols-3 gap-3'>
              {(['light', 'dark', 'system'] as const).map(theme => (
                <button
                  key={theme}
                  type='button'
                  onClick={() =>
                    setValue('theme', theme, { shouldDirty: true })
                  }
                  className={cn(
                    'flex items-center justify-center gap-2 p-3 border rounded-md transition-colors',
                    watch('theme') === theme
                      ? 'border-primary bg-primary/10'
                      : 'border-muted hover:border-primary/50'
                  )}
                >
                  {themeIcons[theme]}
                  <span className='capitalize'>{theme}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Dashboard Layout */}
          <div className='grid gap-2'>
            <Label htmlFor='dashboard_layout'>Dashboard Layout</Label>
            <Select
              value={watch('dashboard_layout')}
              onValueChange={(value: 'grid' | 'list') =>
                setValue('dashboard_layout', value, { shouldDirty: true })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='grid'>Grid View</SelectItem>
                <SelectItem value='list'>List View</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className='bg-transparent'>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>Choose how you want to be notified</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Email Notifications */}
          <div className='flex items-center justify-between'>
            <div className='space-y-0.5'>
              <Label htmlFor='email_notifications'>Email Notifications</Label>
              <p className='text-sm text-muted-foreground'>
                Receive notifications via email
              </p>
            </div>
            <Switch
              id='email_notifications'
              checked={watch('notifications')?.email ?? true}
              onCheckedChange={checked =>
                setValue('notifications.email', checked, { shouldDirty: true })
              }
            />
          </div>

          {/* Push Notifications */}
          <div className='flex items-center justify-between'>
            <div className='space-y-0.5'>
              <Label htmlFor='push_notifications'>Push Notifications</Label>
              <p className='text-sm text-muted-foreground'>
                Receive browser push notifications
              </p>
            </div>
            <Switch
              id='push_notifications'
              checked={watch('notifications')?.push ?? true}
              onCheckedChange={checked =>
                setValue('notifications.push', checked, { shouldDirty: true })
              }
            />
          </div>

          {/* In-App Notifications */}
          <div className='flex items-center justify-between'>
            <div className='space-y-0.5'>
              <Label htmlFor='in_app_notifications'>In-App Notifications</Label>
              <p className='text-sm text-muted-foreground'>
                Show notifications within the app
              </p>
            </div>
            <Switch
              id='in_app_notifications'
              checked={watch('notifications')?.in_app ?? true}
              onCheckedChange={checked =>
                setValue('notifications.in_app', checked, { shouldDirty: true })
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card className='bg-transparent'>
        <CardHeader>
          <CardTitle>Date & Time</CardTitle>
          <CardDescription>
            Configure how dates and times are displayed
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Date Format */}
          <div className='grid gap-2'>
            <Label htmlFor='date_format'>Date Format</Label>
            <Select
              value={watch('date_format')}
              onValueChange={value =>
                setValue('date_format', value, { shouldDirty: true })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='MM/DD/YYYY'>
                  MM/DD/YYYY (12/31/2025)
                </SelectItem>
                <SelectItem value='DD/MM/YYYY'>
                  DD/MM/YYYY (31/12/2025)
                </SelectItem>
                <SelectItem value='YYYY-MM-DD'>
                  YYYY-MM-DD (2025-12-31)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Time Format */}
          <div className='grid gap-2'>
            <Label htmlFor='time_format'>Time Format</Label>
            <Select
              value={watch('time_format')}
              onValueChange={(value: '12h' | '24h') =>
                setValue('time_format', value, { shouldDirty: true })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='12h'>12-hour (2:30 PM)</SelectItem>
                <SelectItem value='24h'>24-hour (14:30)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className='flex justify-end'>
        <Button
          type='submit'
          disabled={!isValid || !isDirty || updatePreferencesMutation.isPending}
        >
          {updatePreferencesMutation.isPending ? (
            <>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Saving...
            </>
          ) : (
            'Save Preferences'
          )}
        </Button>
      </div>
    </form>
  )
}
